import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, Grid, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import PropTypes from 'prop-types';
import { useEffect, useState, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { useParams, useNavigate } from 'react-router-dom';
import { saveBudgetBreakdownData } from '../../../../redux/slices/orders';
import { useDispatch, useSelector } from '../../../../redux/store';
import Iconify from '../../../../reusable-components/iconify';
import { useTabs } from '../../../../context/TabContext';
import { fDateMonth } from '../../../../utils/formatTime';

BudgetBreakdown.propTypes = {
  selectedRow: PropTypes.object,
  fetchOrderLogData: PropTypes.func,
  disabled: PropTypes.bool,
};

export default function BudgetBreakdown({ selectedRow, fetchOrderLogData, disabled = false }) {
  const { id = '' } = useParams();
  const [localUnitPrice, setLocalUnitPrice] = useState(0);
  const [localTax, setLocalTax] = useState(0);
  const [localShipping, setLocalShipping] = useState(0);
  const [localQty, setLocalQty] = useState(0);
  const [changesMade, setChangesMade] = useState(false);
  const [localBudgetSource, setLocalBudgetSource] = useState(0);
  const [localBudgetSourceData, setLocalBudgetSourceData] = useState([]);
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const { addTab } = useTabs();
  const navigate = useNavigate();

  useEffect(() => {
    setLocalData();
  }, []);

  const isLocalBudgetSourceInv = useMemo(
    () => localBudgetSource && localBudgetSource.includes('INV#'),
    [localBudgetSource]
  );

  const openInvoiceInNewTab = (index) => {
    const invoiceId = selectedRow.budgetInvoiceId[index];
    const invoiceType = selectedRow.budgetInvoiceSent[index] ? 'previous' : 'pending';
    addTab();
    navigate(`/dashboard/invoicing/${invoiceType}/${invoiceId}`);
  };

  const setLocalData = () => {
    setLocalUnitPrice(selectedRow.budgetPrice);
    setLocalQty(selectedRow.budgetQty);
    setLocalShipping(selectedRow.budgetShipping);
    setLocalTax(selectedRow.budgetTax);
    setLocalBudgetSource(selectedRow.budgetSource);
    setLocalBudgetSourceData(selectedRow.budgetSourceData);
    setChangesMade(false);
  };

  const handleClose = () => {
    setOpen(false);
    setLocalData();
  };

  const saveBudget = () => {
    if (Number(localUnitPrice) !== selectedRow.budgetPrice[0] || Number(localQty) !== selectedRow.budgetQty[0] ||
        Number(localShipping) !== selectedRow.budgetShipping[0] || Number(localTax) !== selectedRow.budgetTax[0]) {
      dispatch(saveBudgetBreakdownData(id, selectedRow.id, localUnitPrice, localShipping, localTax, localQty)).then(() => fetchOrderLogData());
      setLocalBudgetSource('MANUAL');
      setLocalBudgetSourceData([`${user.firstName} ${user.lastName}`]);
    }
    setOpen(false);
  };

  const getTotalDataForArray = () => {
    const totalSum = selectedRow.budgetSourceData?.reduce((accumulator, data, index) => {
      const itemTotal =
        (selectedRow.budgetPrice[index] || 0) * (selectedRow.budgetQty[index] || 0) +
        (selectedRow.budgetTax[index] || 0) +
        (selectedRow.budgetShipping[index] || 0);
      return accumulator + itemTotal;
    }, 0);
    return totalSum;
  };

  const getBudgetSum = (field) =>
    Array.isArray(selectedRow[field])
      ? selectedRow[field].reduce((sum, value) => sum + Number(value), 0)
      : Number(selectedRow[field]) || 0;

  const normalizeBudgetValue = (value) => {
    const rawValue = Array.isArray(value) ? value[0] : value;
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  };

  const isQuantityUpdatedSaved = selectedRow.quantity !== getBudgetSum('budgetQty');
  const isQuantityUpdatedLocal = normalizeBudgetValue(localQty) !== selectedRow.quantity;
  const isPriceUpdatedSaved = selectedRow.price !== getBudgetSum('budgetPrice');
  const isPriceUpdatedLocal = normalizeBudgetValue(localUnitPrice)?.toFixed(2) !== selectedRow.price.toFixed(2);
  // tax and shipping on an order always start off as 0
  const isShippingUpdatedSaved = getBudgetSum('budgetShipping') > 0;
  const isShippingUpdatedLocal = normalizeBudgetValue(localShipping) > 0;
  const isTaxUpdatedSaved = getBudgetSum('budgetTax') > 0;
  const isTaxUpdatedLocal = normalizeBudgetValue(localTax) > 0;

  const isBudgetUpdatedSaved =
    isQuantityUpdatedSaved || isShippingUpdatedSaved || isTaxUpdatedSaved || isPriceUpdatedSaved;

  const budgetBreakdownLastUpdatedText = `Last updated on ${fDateMonth(selectedRow.budgetUpdatedAt)} by ${localBudgetSourceData.map((data) => `${localBudgetSource === 'MANUAL' ? '' : localBudgetSource} ${data}`).join(', ')}`;

  const budgetMenu = (
    <Box sx={{ p: 1 }}>
      <Grid container color="common.black" direction="column" textAlign="center">
        <Grid item sx={{ mt: 1 }}>
          <Typography color="secondary" fontWeight="bold">
            Budget Breakdown
          </Typography>
        </Grid>
        {selectedRow.budgetSourceData?.map((row, index) => (
          <span key={index}>
            <Grid item>
              <Typography fontSize={12}>
                {localBudgetSource === 'MANUAL' ? (
                  `Data adjusted by ${localBudgetSourceData}`
                ) : (
                  <span>
                    {`Data pulling from `}
                    {
                      <Typography
                        display="inline"
                        fontSize={12}
                        sx={{
                          cursor: isLocalBudgetSourceInv ? 'pointer' : '',
                          textDecoration: isLocalBudgetSourceInv ? 'underline' : '',
                        }}
                        color={isLocalBudgetSourceInv ? 'secondary' : ''}
                        onClick={isLocalBudgetSourceInv ? () => openInvoiceInNewTab(index) : undefined}
                      >
                        {selectedRow.budgetSourceData?.length > 1
                          ? selectedRow.budgetSource[index] + selectedRow.budgetSourceData[index]
                          : localBudgetSource + localBudgetSourceData}
                      </Typography>
                    }
                  </span>
                )}
              </Typography>
              <Stack
                spacing={1}
                direction="row"
                justifyContent="center"
                alignItems="center"
                sx={{
                  mt: 1,
                }}
              >
                <Typography>Unit Price:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isPriceUpdatedLocal && (
                    <Tooltip
                      disableHoverListener={!isPriceUpdatedLocal}
                      position="top"
                      arrow
                      title={<div> Reset to PO Price: ${selectedRow.price?.toFixed(2)}</div>}
                    >
                      <IconButton color="info" onClick={() => setLocalUnitPrice(selectedRow.price)} sx={{ p: 0 }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <NumericFormat
                    value={
                      selectedRow.budgetSourceData?.length > 1
                        ? `${selectedRow.budgetPrice[index]}`
                        : `${localUnitPrice}`
                    }
                    customInput={TextField}
                    sx={{
                      width: 'auto',
                      maxWidth: 100,
                      '& .MuiInputBase-root': {
                        color: selectedRow.budgetSource === 'MANUAL' ? 'secondary.main' : 'primary.main',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: selectedRow.budgetSource === 'MANUAL' ? 'secondary.main' : 'primary.main',
                      },
                    }}
                    variant="standard"
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    onValueChange={(values) => {
                      const { value } = values;
                      setLocalUnitPrice(value);
                      setChangesMade(true);
                    }}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1} direction="row" justifyContent="center" alignItems="center">
                <Typography>Quantity:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isQuantityUpdatedLocal && (
                    <Tooltip
                      disableHoverListener={!isQuantityUpdatedLocal}
                      position="top"
                      arrow
                      title={<div>Reset to PO Quantity: {selectedRow.quantity}</div>}
                    >
                      <IconButton color="info" onClick={() => setLocalQty(selectedRow.quantity)} sx={{ p: 0 }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <TextField
                    sx={{ maxWidth: 100 }}
                    value={selectedRow.budgetSourceData?.length > 1 ? selectedRow.budgetQty[index] : localQty}
                    color="primary"
                    variant="standard"
                    type="number"
                    onChange={(e) => {
                      setLocalQty(e.target.value);
                      setChangesMade(true);
                    }}
                    autoComplete="off"
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1} direction="row" justifyContent="center" alignItems="center">
                <Typography>Shipping:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isShippingUpdatedLocal && (
                    <Tooltip
                      disableHoverListener={!isShippingUpdatedLocal}
                      position="top"
                      arrow
                      title={<div>Reset to PO Shipping: $0</div>}
                    >
                      <IconButton color="info" onClick={() => setLocalShipping(0)} sx={{ p: 0 }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <NumericFormat
                    value={
                      selectedRow.budgetSourceData?.length > 1
                        ? `${selectedRow.budgetShipping[index]}`
                        : `${localShipping}`
                    }
                    customInput={TextField}
                    sx={{ maxWidth: 100 }}
                    color="primary"
                    variant="standard"
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    onValueChange={(values) => {
                      const { value } = values;
                      setLocalShipping(value);
                      setChangesMade(true);
                    }}
                  />
                </Box>
              </Stack>
            </Grid>
            <Grid item>
              <Stack spacing={1} direction="row" justifyContent="center" alignItems="center">
                <Typography>Tax:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isTaxUpdatedLocal && (
                    <Tooltip
                      disableHoverListener={!isTaxUpdatedLocal}
                      position="top"
                      arrow
                      title={<div>Reset to PO Tax: $0</div>}
                    >
                      <IconButton color="info" onClick={() => setLocalTax(0)} sx={{ p: 0 }}>
                        <RestartAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <NumericFormat
                    sx={{ maxWidth: 100 }}
                    value={selectedRow.budgetSourceData?.length > 1 ? `${selectedRow.budgetTax[index]}` : `${localTax}`}
                    color="primary"
                    variant="standard"
                    customInput={TextField}
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    prefix="$"
                    onValueChange={(values) => {
                      const { value } = values;
                      setLocalTax(value);
                      setChangesMade(true);
                    }}
                  />
                </Box>
              </Stack>
            </Grid>
          </span>
        ))}
        {changesMade ? (
          <Grid item>
            <Button
              disabled={selectedRow.budgetSourceData?.length > 1}
              variant="contained"
              color="primary"
              size="small"
              sx={{
                minWidth: 120,
                marginTop: 2,
              }}
              onClick={saveBudget}
            >
              Save
            </Button>
          </Grid>
        ) : (
          <Grid sx={{ p: 1 }} />
        )}
      </Grid>
    </Box>
  );

  return (
    <Box display="flex" alignItems="center">
      <Tooltip arrow title={budgetBreakdownLastUpdatedText} disableHoverListener={!isBudgetUpdatedSaved || disabled}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isBudgetUpdatedSaved && <Iconify icon="mdi:update" width={15} />}
          <Box>
            $
            {(selectedRow.budgetSourceData?.length > 1
              ? getTotalDataForArray()
              : localQty * localUnitPrice + Number(localTax) + Number(localShipping)
            ).toFixed(2)}
          </Box>
        </Box>
      </Tooltip>
      <ClickAwayListener onClickAway={handleClose}>
        <span style={{ height: 20 }}>
          <Tooltip
            placement="bottom-end"
            title={budgetMenu}
            arrow
            onClose={handleClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#fff',
                  borderRadius: '6px',
                  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 6px 0px',
                  backgroundColor: 'rgb(255, 255, 255)',
                  border: '1px solid rgb(193, 201, 208)',
                  '& .MuiTooltip-arrow': {
                    color: '#fff',
                    '&:before': {
                      boxShadow: 'rgba(0, 0, 0, 0.16) 0px 3px 6px 0px',
                      backgroundColor: 'rgb(255, 255, 255)',
                      border: '1px solid rgb(193, 201, 208)',
                    },
                  },
                },
              },
            }}
          >
            <span>
              <Tooltip title={open ? '' : 'Budget Breakdown'} arrow disableHoverListener={disabled}>
                <Iconify
                  color="secondary"
                  onClick={() => {
                    if (!disabled) setOpen(true);
                  }}
                  sx={{
                    opacity: disabled ? 0.5 : 0.8,
                    color: disabled ? 'text.disabled' : undefined,
                  }}
                  icon="basil:settings-adjust-outline"
                />
              </Tooltip>
            </span>
          </Tooltip>
        </span>
      </ClickAwayListener>
    </Box>
  );
}
